import {
  Badge,
  Box,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import { ReactNode } from "react";
import { AppButton } from "../Button/AppButton";
import { StatusChip } from "../StatusChip/StatusChip";

/** Shared report filter toolbar surface (Filing Report standard). */
export const FILTER_BAR_PAPER_SX = {
  px: 1.5,
  py: 1,
  flexShrink: 0,
  bgcolor: "grey.50",
} as const;

export type FilterBarChip = {
  key: string;
  label: ReactNode;
  onDelete?: () => void;
};

export type FilterBarProps = {
  /** Left-side controls (search field, selects, Load Report, etc.). */
  leading?: ReactNode;
  /** Extra toolbar actions after Filters / Clear. */
  trailing?: ReactNode;
  activeCount?: number;
  filtersActive?: boolean;
  clearDisabled?: boolean;
  onOpenFilters?: () => void;
  onClear?: () => void;
  chips?: FilterBarChip[];
  filterButtonLabel?: string;
  clearButtonLabel?: string;
  hideFilterButton?: boolean;
  hideClearButton?: boolean;
  children?: ReactNode;
};

/** Shared filter toolbar: controls + Filters drawer trigger + active chips. */
export function FilterBar({
  leading,
  trailing,
  activeCount = 0,
  filtersActive = false,
  clearDisabled,
  onOpenFilters,
  onClear,
  chips = [],
  filterButtonLabel = "Filters",
  clearButtonLabel = "Clear filters",
  hideFilterButton = false,
  hideClearButton = false,
  children,
}: FilterBarProps) {
  return (
    <Paper variant="outlined" sx={FILTER_BAR_PAPER_SX}>
      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        useFlexGap
        alignItems="center"
      >
        {leading}
        {!hideFilterButton && onOpenFilters && (
          <Badge
            badgeContent={activeCount}
            color="primary"
            overlap="rectangular"
            sx={{ "& .MuiBadge-badge": { right: 6, top: 6 } }}
          >
            <AppButton
              variant={filtersActive ? "contained" : "outlined"}
              color={filtersActive ? "primary" : "inherit"}
              size="small"
              startIcon={<FilterListIcon />}
              onClick={onOpenFilters}
              sx={filtersActive ? undefined : { bgcolor: "background.paper" }}
            >
              {filterButtonLabel}
            </AppButton>
          </Badge>
        )}
        {!hideClearButton && onClear && (
          <AppButton
            variant="contained"
            color="warning"
            size="small"
            startIcon={<FilterAltOffIcon />}
            onClick={onClear}
            disabled={clearDisabled ?? !filtersActive}
          >
            {clearButtonLabel}
          </AppButton>
        )}
        {trailing}
        {children}
      </Stack>

      {chips.length > 0 && (
        <Stack
          direction="row"
          spacing={0.75}
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: 1 }}
        >
          {chips.map((chip) => (
            <StatusChip
              key={chip.key}
              size="small"
              label={chip.label}
              onDelete={chip.onDelete}
              sx={{ bgcolor: "background.paper" }}
            />
          ))}
        </Stack>
      )}
    </Paper>
  );
}

export type FilterDrawerProps = {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  activeCount?: number;
  children: ReactNode;
  actions?: ReactNode;
};

/** Right-side filter drawer shell used with FilterBar. */
export function FilterDrawer({
  open,
  onClose,
  title = "Filters",
  subtitle,
  activeCount = 0,
  children,
  actions,
}: FilterDrawerProps) {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 420 },
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle1" fontWeight={600}>
              {title}
            </Typography>
            {activeCount > 0 && (
              <StatusChip
                size="small"
                tone="primary"
                label={`${activeCount} active`}
                sx={{ height: 22, fontWeight: 600, fontSize: "0.7rem" }}
              />
            )}
          </Stack>
          {subtitle != null && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <IconButton aria-label="Close filters" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Stack>

      <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 2 }}>{children}</Box>

      {actions != null && (
        <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: "divider", bgcolor: "grey.50" }}>
          <Stack direction="row" spacing={1}>
            {actions}
          </Stack>
        </Box>
      )}
    </Drawer>
  );
}

export default FilterBar;
